import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const heroImages = [
  "https://www.parksidepavers.com/files/projects/14/images/thumb/thumb_large_IMG%5F2835%2Ejpg",
  "https://www.parksidepavers.com/files/projects/15/images/thumb/thumb_large_Albano%2Dlanai%2Ejpg",
  "https://www.parksidepavers.com/files/projects/16/images/thumb/thumb_large_IMG%5F2840%2Ejpg",
];

const stats = [
  { value: '500+', label: 'Projects Completed' },
  { value: '5.0★', label: 'Google Rating' },
  { value: '10+', label: 'Years Experience' },
  { value: '100%', label: 'Satisfaction Rate' },
];

export default function HeroSection() {
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImg((prev) => (prev + 1) % heroImages.length);
    }, 5500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-end bg-parkside-darker overflow-hidden">
      {/* Background image slideshow */}
      {heroImages.map((img, i) => (
        <div
          key={img}
          className="absolute inset-0 transition-opacity duration-1500"
          style={{
            opacity: activeImg === i ? 1 : 0,
            backgroundImage: `url('${img}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
          }}
        />
      ))}

      {/* Multi-layer gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-parkside-darker via-parkside-darker/50 to-parkside-darker/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-parkside-darker/60 to-transparent" />

      {/* Image indicator dots */}
      <div className="absolute bottom-[260px] left-1/2 -translate-x-1/2 lg:left-auto lg:translate-x-0 lg:right-8 flex gap-2 z-10">
        {heroImages.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveImg(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              activeImg === i ? 'w-6 h-2 bg-parkside-amber' : 'w-2 h-2 bg-white/30 hover:bg-white/60'
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pb-0">
        <div className="max-w-3xl pt-16 pb-12">
          {/* Trust badges */}
          <div className="flex flex-wrap gap-2 mb-7 animate-fade-in">
            {['✔ Licensed & Insured', '✔ ICPI Certified', '✔ 5-Star Rated', '✔ Free Estimates'].map((badge) => (
              <span
                key={badge}
                className="bg-white/10 backdrop-blur-sm text-white/90 text-xs font-semibold px-3.5 py-1.5 rounded-full border border-white/20"
              >
                {badge}
              </span>
            ))}
          </div>

          {/* Headline */}
          <h1 className="font-display text-5xl md:text-6xl lg:text-[72px] text-white leading-[1.08] tracking-display mb-6 animate-fade-in-up">
            Premium Paver Installation &amp;{' '}
            <span className="text-parkside-gold italic">Outdoor Living</span>{' '}
            Design Built to Last
          </h1>

          <p className="text-white/75 text-lg md:text-xl leading-relaxed mb-8 max-w-xl animate-fade-in-up delay-100">
            Custom driveways, patios, walkways, and outdoor spaces designed for durability, beauty, and long-term property value throughout Tampa Bay.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4 mb-10 animate-fade-in-up delay-200">
            <Link
              to="/contact"
              className="bg-parkside-amber text-white font-bold px-8 py-4 rounded-xl hover:bg-parkside-amber/90 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-base"
            >
              Get a Free Estimate
              <span className="w-5 h-5 inline-flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
            <a
              href="tel:813-404-6313"
              className="bg-white/12 backdrop-blur-sm border border-white/30 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/22 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-base"
            >
              <span className="w-5 h-5 inline-flex items-center justify-center">
                <i className="ri-phone-fill" />
              </span>
              Call (813) 404-6313
            </a>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="relative z-10 w-full border-t border-white/10 bg-parkside-darker/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 lg:divide-x lg:divide-white/15">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center lg:px-8">
                <p className="font-display text-3xl text-parkside-gold">{stat.value}</p>
                <p className="text-white/55 text-xs font-semibold mt-0.5 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
